import React from 'react';
import SearchAndPagination from './SearchAndPagination';

/**
 * ActiveSessionsTab
 *
 * Props:
 * - sessions: array
 * - activeTab: 'active' | 'scheduled'
 * - handleResumeSession: (sessionId) => void
 * - handleViewResults: (session) => void
 * - handleStartNewSession: () => void
 * - getStatusBadge: (status) => ReactNode
 * - formatTime: (timestamp) => string
 */
const ActiveSessionsTab = ({
  sessions,
  activeTab,
  handleResumeSession,
  handleViewResults,
  handleStartNewSession,
  getStatusBadge,
  formatTime
}) => {
  const filteredSessions = sessions.filter((session) => {
    if (activeTab === 'active') return session.status === 'active' || session.status === 'in_progress';
    // Scheduled tab: Only show sessions with status='pending' (user-requested consultant sessions)
    if (activeTab === 'scheduled') return session.status === 'pending';
    return false;
  });

  const renderSessions = (sessionsToRender) => {
    if (!sessionsToRender.length) {
      return (
        <div className="p-8 text-center text-gray-500 dark:text-gray-400">
          <svg
            className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <p>No {activeTab} sessions found</p>
        </div>
      );
    }

    return sessionsToRender.map((session) => (
    <div key={session.id} className="p-4 sm:p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
            <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white truncate">{session.clientName}</h3>
            <div className="flex-shrink-0">
              {getStatusBadge(session.status)}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 text-sm text-gray-600 dark:text-gray-400">
            <div className="min-w-0">
              <span className="font-medium">Industry:</span>{' '}
              <span className="truncate block sm:inline">{session.industry || 'Not specified'}</span>
            </div>
            <div className="min-w-0">
              <span className="font-medium">Email:</span>{' '}
              <span className="truncate block sm:inline">{session.clientEmail || 'N/A'}</span>
            </div>
            <div className="min-w-0">
              <span className="font-medium">
                {session.status === 'scheduled' || session.status === 'pending' ? 'Scheduled:' : 'Started:'}
              </span>{' '}
              <span className="whitespace-nowrap">
                {session.scheduledTime || session.startTime
                  ? formatTime(session.scheduledTime || session.startTime)
                  : 'N/A'}
              </span>
              {activeTab === 'scheduled' && (
                <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                  session.status === 'pending'
                    ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300'
                    : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300'
                }`}>
                  {session.status === 'pending' ? 'Pending' : 'Done'}
                </span>
              )}
            </div>
          </div>

          {session.status === 'in_progress' && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">
                  Progress: {session.questionsCompleted}/{session.totalQuestions} questions
                </span>
                <span className="text-gray-600 dark:text-gray-400">{session.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-1">
                <div
                  className="bg-emerald-600 dark:bg-emerald-500 h-2 rounded-full"
                  style={{ width: `${session.progress}%` }}
                ></div>
              </div>
            </div>
          )}

          {session.status === 'completed' && session.potentialSavings && (
            <div className="mt-2 text-sm">
              <span className="font-medium text-emerald-600 dark:text-emerald-400">
                Potential Savings: ${session.potentialSavings.toLocaleString()}/year
              </span>
            </div>
          )}

          {session.clientNotes && (
            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              <span className="font-medium">Notes:</span> {session.clientNotes}
            </div>
          )}
        </div>

        <div className="flex flex-row sm:flex-col gap-2 sm:gap-2 sm:ml-6 sm:flex-shrink-0">
          {(session.status === 'active' || session.status === 'in_progress') && (
            <button
              onClick={() => handleResumeSession(session.id)}
              className="flex-1 sm:flex-none bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
            >
              Resume Session
            </button>
          )}

          {session.status === 'completed' && (
            <button
              onClick={() => handleViewResults(session)}
              className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              disabled={!session.assessmentId}
            >
              View Results
            </button>
          )}
        </div>
      </div>
    </div>
    ));
  };

  if (!filteredSessions.length) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
        <svg
          className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
        <p>No {activeTab} sessions found</p>
      </div>
    );
  }

  return (
    <SearchAndPagination
      data={filteredSessions}
      searchFields={['clientName', 'clientEmail', 'industry']}
      itemsPerPage={10}
      searchPlaceholder={`Search ${activeTab} sessions by name, email, or industry...`}
      renderItems={renderSessions}
    />
  );
};

export default ActiveSessionsTab;


